import { PhoneCountryCodes } from '@/constants/countries';

/**
 * Transforms a full name into abbreviated format (e.g., "John Doe" -> "J. Doe")
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} Abbreviated name format
 */
export const abbreviateName = (firstName, lastName) => {
  if (!firstName) return lastName;
  else if (!lastName) return firstName;
  return `${firstName.charAt(0)}. ${lastName}`;
};

/**
 * Truncates a string to a specified length and adds an ellipsis if it exceeds the length
 * @param {string} string - The string to truncate
 * @param {number} maxLength - The maximum length of the string
 * @returns {string} The truncated string
 */
export const truncate = (string, maxLength = 10) => {
  if (string?.length <= maxLength) return string;
  return `${string.slice(0, maxLength)}...`;
};

/**

 * Capitalizes the first letter of each word in the given string.
 *
 * @param {string} string - The input string to capitalize.
 * @returns {string} The input string with the first letter of each word capitalized.
 *
 * @example
 * capitalizeWords('hello world'); // Returns: 'Hello World'
 * @example
 * capitalizeWords('javaScript is awesome'); // Returns: 'JavaScript Is Awesome'
 */
export const capitalizeWords = (string) => {
  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)?.toLowerCase())
    .join(' ');
};

/**
 * Very lazy method to make a word plural
 * @param {number} count
 * @param {string} word
 * @returns {string}
 */
export const pluralize = (count, word) => {
  return count === 1 ? word : `${word}s`;
};

/**
 * Replaces all underscores in a string with spaces.
 *
 * @param {string} str - The input string to process.
 * @returns {string} A new string with all underscores replaced by spaces.
 *
 * @example
 * stripUnderscores('Hello_World') // returns 'Hello World'
 */
export const stripUnderscores = (str) => {
  return str.replace(/_/g, ' ');
};

/**
 * Converts a string into a simple slug using underscores.
 *
 * The transformation:
 * - Converts all characters to lowercase.
 * - Trims leading and trailing whitespace.
 * - Replaces one or more whitespace characters with a single underscore.
 *
 * @param {string} str - The input string to slugify.
 * @returns {string} The slugified string.
 *
 * @example
 * slugify('Hello World'); // 'hello_world'
 * @example
 * slugify('  Hello   World  '); // 'hello_world'
 */
export const slugify = (str) => {
  const normalized = str.toLowerCase().trim();
  return normalized.replace(/\s+/g, '_');
};

/**
 * Replaces underscores with spaces and capitalizes the first letter of the result.
 * @param {string} str - The input string.
 * @returns {string} The formatted string, with only the first character capitalized.
 * @example
 * capitalizeAndStripUnderscores('routing_number') // 'Routing number'
 */
export function capitalizeAndStripUnderscores(str) {
  const withSpaces = stripUnderscores(str);
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

/**
 * Function that returns country code by provided phone number.
 * Tries to match the longest phone prefix first (up to 4 chars including +).
 * @param {String} phone - The phone number to get the country code from.
 * @returns {String|undefined} The country code, or undefined if not found.
 * @example
 * getPhoneCountryCode('+12025551234') // 'US'
 * getPhoneCountryCode('+447911123456') // 'GB'
 */
export const getPhoneCountryCode = (phone) => {
  if (!phone || !phone.startsWith('+')) return undefined;

  for (let len = 4; len >= 2; len--) {
    const prefix = phone.slice(0, len);
    if (PhoneCountryCodes[prefix]) {
      return PhoneCountryCodes[prefix];
    }
  }

  return undefined;
};
